import { Line } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import type { ComponentProps, ElementRef } from "react";
import { useEffect, useMemo, useRef } from "react";
import {
  type Group,
  type Mesh,
  MeshStandardMaterial,
  type Object3D,
  SphereGeometry,
} from "three";
import type { FlightCaseBlueprint } from "../interfaces/FlightCaseBlueprint";

// AnimatedLine: wraps Drei's <Line> with animated dashed dashOffset.
type LineComponentProps = ComponentProps<typeof Line>;

const AnimatedLine = (props: LineComponentProps) => {
  const lineRef = useRef<ElementRef<typeof Line> | null>(null);
  useFrame((_state, delta) => {
    if (typeof lineRef.current?.material?.dashOffset === "number") {
      lineRef.current.material.dashOffset -= delta * 10;
    }
  }, -0.75);
  return <Line ref={lineRef} {...props} />;
};

// FancyCorner: renders a pulsating sphere with emissive glow.
const FancyCorner = ({
  position,
  ...props
}: {
  position: { x: number; y: number; z: number };
}) => {
  const meshRef = useRef<Mesh | null>(null);
  useFrame((state) => {
    if (meshRef.current) {
      const scale = 1 + 0.2 * Math.sin(state.clock.getElapsedTime() * 3);
      meshRef.current.scale.set(scale, scale, scale);
    }
  }, -0.75);
  const geometry = useMemo(() => new SphereGeometry(5, 16, 16), []);
  const material = useMemo(
    () =>
      new MeshStandardMaterial({
        color: "red",
        emissive: "red",
        emissiveIntensity: 0.5,
      }),
    [],
  );
  useEffect(() => {
    if (!meshRef.current) return;
    const mesh = meshRef.current;
    mesh.position.set(position.x, position.y, position.z);
    mesh.geometry = geometry;
    mesh.material = material;
  }, [position, geometry, material]);
  return <mesh ref={meshRef} {...props} />;
};

export const FligthcaseBlueprintRenderer = ({
  blueprint,
}: {
  blueprint: FlightCaseBlueprint;
}) => {
  const group = useRef<Group>(null);
  useFrame((state) => {
    if (
      !group.current ||
      (!blueprint.corners.length && !blueprint.edges.length)
    )
      return;
    let parent: Object3D | null = group.current;
    while (parent) {
      if (!parent.visible) return;
      parent = parent.parent;
    }
    // `invalidate` alone, and deliberately not `markProductSceneChanged`: the
    // only thing moving here is the dash offset of a line, and a scene
    // revision bump wakes everything that watches the product for a *shape*
    // change — the contact shadow re-capture, the hardware instancing flush,
    // the floor-alignment bounding-box walk. Bumping it every frame would keep
    // all three of them running for as long as a blueprint is on screen.
    state.invalidate();
  }, -0.6);

  return (
    <group ref={group}>
      {/* Render corners as pulsating red spheres */}
      {blueprint.corners.map((corner) => (
        <FancyCorner
          key={`corner-${corner.position.x}-${corner.position.y}-${corner.position.z}`}
          position={corner.position}
        />
      ))}
      {/* Render edges as blue outer lines and green inner lines (if available) */}
      {blueprint.edges.map((edge) => {
        const outerStart = edge.start.outerPos;
        const outerEnd = edge.end.outerPos;
        const outerLine = (
          <AnimatedLine
            key={`edge-outer-${outerStart.x}-${outerStart.y}-${outerStart.z}-${outerEnd.x}-${outerEnd.y}-${outerEnd.z}`}
            points={[
              [outerStart.x, outerStart.y, outerStart.z],
              [outerEnd.x, outerEnd.y, outerEnd.z],
            ]}
            color="blue"
            lineWidth={2}
            dashed
            dashScale={0.5}
            dashSize={10}
          />
        );

        let innerLine = null;
        if (edge.start.innerPos && edge.end.innerPos) {
          innerLine = (
            <AnimatedLine
              key={`edge-inner-${edge.start.innerPos.x}-${edge.start.innerPos.y}-${edge.start.innerPos.z}-${edge.end.innerPos.x}-${edge.end.innerPos.y}-${edge.end.innerPos.z}`}
              points={[
                [
                  edge.start.innerPos.x,
                  edge.start.innerPos.y,
                  edge.start.innerPos.z,
                ],
                [edge.end.innerPos.x, edge.end.innerPos.y, edge.end.innerPos.z],
              ]}
              color="green"
              lineWidth={2}
              dashed
              dashScale={0.5}
              dashSize={10}
            />
          );
        }

        return (
          <group
            key={`edge-group-${outerStart.x}-${outerStart.y}-${outerStart.z}-${outerEnd.x}-${outerEnd.y}-${outerEnd.z}`}
          >
            {outerLine}
            {innerLine}
          </group>
        );
      })}
    </group>
  );
};
